import React, { useEffect, useState } from "react";
import axiosInstance from '../../api/axios';
import {
    Row,
    Col,
    Button,
    Form,
    Table,
    Modal,
    Badge,
    Spinner,
    Pagination,
    InputGroup,
    FormControl,
    Card,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import { ArrowLeft, FileEarmarkSpreadsheet } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import OutletSelect from "../../components/OutletSelect";

const OnlineSalesReport = () => {

    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem("authToken");
    const navigate = useNavigate();

    // Filter states
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [platform, setPlatform] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [isCosting, setIsCosting] = useState(false);
    const [searchText, setSearchText] = useState("");

    // Data states
    const [salesData, setSalesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [subBtnLoading, setSubBtnLoading] = useState(false);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [recordsPerPage, setRecordsPerPage] = useState(25);

    // Summary totals
    const [grandTotal, setGrandTotal] = useState(0);
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [totalQuantity, setTotalQuantity] = useState(0);

    // View detail modal
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [viewDetailsModal, setViewDetailsModal] = useState(false);

    // Categories state (fetched or static)
    const [categories, setCategories] = useState([]);

    const getOnlineSalesData = async (
        start,
        end,
        outlet,
        page,
        limit,
        search,
        plat,
        cat,
        costing,
        download
    ) => {
        try {
            setLoading(true);

            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            let url = `third-party-sales?`;

            if (outlet) url += `outlet=${outlet}&`;
            if (start) url += `startDate=${start.toISOString().split("T")[0]}&`;
            if (end) url += `endDate=${end.toISOString().split("T")[0]}&`;
            if (plat) url += `platform=${plat}&`;
            if (cat) url += `category=${cat}&`;
            if (costing) url += `isCosting=${costing}&`;
            if (download) {
                url += `isDownload=true&`;
            } else {
                if (search) {
                    url += `search=${search}&`;
                } else {
                    url += `page=${page}&limit=${limit}&`;
                }
            }

            // Remove trailing &
            url = url.replace(/&$/, "");

            const response = await axiosInstance.get(url, {
                headers,
                responseType: download ? "blob" : "json",
            });

            if (download) {
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = `${selectedOutlet?.label || "All Outlets"} Online Sales Report (${end.toISOString().split("T")[0]}).xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                toast.success("Excel downloaded successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            } else {
                const records = response?.data?.data?.dbResponse || [];
                if (records.length > 0) {
                    setSalesData(records);
                    setFilteredData(records);

                    // Calculate totals from response
                    const gt = records.reduce((sum, r) => sum + (r.total || 0), 0);
                    const td = records.reduce((sum, r) => sum + (r.discount || 0), 0);
                    const tq = records.reduce((sum, r) => sum + (r.quantity || 0), 0);
                    setGrandTotal(gt);
                    setTotalDiscount(td);
                    setTotalQuantity(tq);

                    // If pagination data is returned from API use it, otherwise derive
                    const paginationData = response?.data?.data?.paginationData;
                    if (paginationData) {
                        setTotalPages(paginationData.totalPages || 1);
                        setTotalRecords(paginationData.totalRecords || records.length);
                    } else {
                        setTotalPages(Math.ceil(records.length / limit) || 1);
                        setTotalRecords(records.length);
                    }

                    toast.success("Data fetched successfully", {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "light",
                    });
                } else {
                    setSalesData([]);
                    setFilteredData([]);
                    setGrandTotal(0);
                    setTotalDiscount(0);
                    setTotalQuantity(0);
                    setTotalPages(0);
                    setTotalRecords(0);
                    toast.info("No records found.", {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "light",
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching online sales data:", error);
            setFilteredData([]);
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) {
                    errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.error;
                } else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error;
                } else {
                    errorMessage = "Something went wrong. Please try again.";
                }
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setLoading(false);
            setDownloadBtnLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSubBtnLoading(true);
        setClicked(true);
        setCurrentPage(1);
        await getOnlineSalesData(
            startDate,
            endDate,
            selectedOutlet?.value || "",
            1,
            recordsPerPage,
            searchText,
            platform,
            selectedCategory,
            isCosting
        );
        setSubBtnLoading(false);
    };

    const handleDownloadExcel = () => {
        setDownloadBtnLoading(true);
        getOnlineSalesData(
            startDate,
            endDate,
            selectedOutlet?.value || "",
            currentPage,
            recordsPerPage,
            searchText,
            platform,
            selectedCategory,
            isCosting,
            true
        );
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
            getOnlineSalesData(
                startDate,
                endDate,
                selectedOutlet?.value || "",
                page,
                recordsPerPage,
                searchText,
                platform,
                selectedCategory,
                isCosting
            );
        }
    };

    useEffect(() => {
        if (clicked) {
            getOnlineSalesData(
                startDate,
                endDate,
                selectedOutlet?.value || "",
                1,
                recordsPerPage,
                searchText,
                platform,
                selectedCategory,
                isCosting
            );
        }
    }, [recordsPerPage]);

    const getPlatformBadgeColor = (plat) => {
        switch (plat) {
            case "ZOMATO":
                return "danger";
            case "SWIGGY":
                return "warning";
            case "MAGIC_PIN":
                return "info";
            default:
                return "secondary";
        }
    };

    const renderPaginationItems = () => {
        if (totalPages > 15) {
            return (
                <>
                    {currentPage > 3 && (
                        <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                    )}
                    {currentPage > 4 && <Pagination.Ellipsis />}
                    {Array.from({ length: 5 }, (_, i) => {
                        const pageNumber = currentPage > 3 ? currentPage - 2 + i : i + 1;
                        if (pageNumber <= totalPages) {
                            return (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            );
                        }
                        return null;
                    })}
                    {currentPage < totalPages - 3 && <Pagination.Ellipsis />}
                    {currentPage < totalPages - 2 && (
                        <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                            {totalPages}
                        </Pagination.Item>
                    )}
                </>
            );
        } else {
            return Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                    key={i + 1}
                    active={i + 1 === currentPage}
                    onClick={() => handlePageChange(i + 1)}
                >
                    {i + 1}
                </Pagination.Item>
            ));
        }
    };

    const viewRecordDetails = (record) => {
        setSelectedRecord(record);
        setViewDetailsModal(true);
    };

    const closeModal = () => {
        setViewDetailsModal(false);
        setSelectedRecord(null);
    };

    const lastPage = Math.ceil(totalRecords / recordsPerPage);

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));

        setStartDate(start);
        setEndDate(end);
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"
                    }`}
            >
                <div>
                    <div
                        className={`card ${skin === "dark" ? "bg-dark-dark" : ""
                            } border-0 shadow-sm`}
                    >
                        {/* Card Header */}
                        <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mb-0 fw-bold d-inline-block">
                                    <i className="bi bi-globe me-2"></i>ONLINE SALES REPORT
                                </h4>
                            </div>
                            <div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate("/reports/all-reports")}
                                    className="me-3 d-flex align-items-center fw-semibold"
                                    style={{ height: "38px" }}
                                >
                                    <ArrowLeft className="me-1" /> Back to Reports
                                </Button>
                            </div>
                        </div>
                        <hr className="mt-0 mb-0" />

                        <div className="card-body">
                            {/* Filters Section */}
                            <div className="filters-section p-3 bg-light rounded-3 mb-3">
                                <Row className="g-3">
                                    {/* Outlet */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <OutletSelect
                                                value={selectedOutlet}
                                                onChange={(option) => setSelectedOutlet(option)}
                                                required={true}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Platform */}
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Platform <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={platform}
                                                onChange={(e) => setPlatform(e.target.value)}
                                                className="border-secondary"
                                            >
                                                <option value="">All Platforms</option>
                                                <option value="ZOMATO">ZOMATO</option>
                                                <option value="SWIGGY">SWIGGY</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Start Date */}
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Start Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Start Date"
                                                className="form-control border-secondary"
                                                maxDate={endDate || new Date()}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* End Date */}
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                End Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                                maxDate={new Date()}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Is Costing */}
                                    <Col md={1} className="d-flex align-items-end pb-1">
                                        <Form.Check
                                            type="checkbox"
                                            label="Costing"
                                            checked={isCosting}
                                            onChange={(e) => setIsCosting(e.target.checked)}
                                            id="is-costing"
                                            className="fw-semibold"
                                        />
                                    </Col>

                                    {/* Generate Button */}
                                    <Col md={2}>
                                        <Form.Label className="fw-light text-light">.</Form.Label>
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            onClick={handleSubmit}
                                            disabled={
                                                subBtnLoading ||
                                                !selectedOutlet ||
                                                !startDate ||
                                                !endDate
                                            }
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Search and Actions Section */}
                            <div className="actions-section mb-3 p-3 bg-light rounded-3">
                                <Row className="g-3 align-items-center">
                                    <Col md={6}>
                                        <InputGroup>
                                            <FormControl
                                                type="text"
                                                placeholder="Search by product name or platform..."
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                className="border-secondary"
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={handleSubmit}
                                                style={{ zIndex: 0 }}
                                                disabled={!selectedOutlet || !startDate || !endDate}
                                            >
                                                <i className="bi bi-search me-1"></i>Search
                                            </Button>
                                        </InputGroup>
                                    </Col>

                                    <Col md={3}>
                                        <Button
                                            variant="outline-primary"
                                            className="w-100"
                                            onClick={handleSubmit}
                                            disabled={!selectedOutlet || !startDate || !endDate}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Refresh Results"
                                            )}
                                        </Button>
                                    </Col>

                                    <Col md={3}>
                                        <Button
                                            variant="outline-success"
                                            className="w-100 fw-semibold"
                                            disabled={
                                                downloadBtnLoading ||
                                                !clicked ||
                                                filteredData?.length === 0
                                            }
                                            onClick={handleDownloadExcel}
                                        >
                                            <FileEarmarkSpreadsheet className="me-2" />
                                            {downloadBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Download Excel"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Summary Cards */}
                            {clicked && filteredData?.length > 0 && (
                                <div className="mb-3">
                                    <Row className="g-2">
                                        <Col md={12}>
                                            <Card
                                                className={`border-0 ${skin === "dark" ? "bg-dark text-light" : "bg-light"
                                                    }`}
                                            >
                                                <Card.Body className="py-2">
                                                    <Row className="text-center">
                                                        <Col md={3} className="border-end">
                                                            <small
                                                                className={`text-muted ${skin === "dark" ? "text-light" : ""
                                                                    }`}
                                                            >
                                                                Total Records
                                                            </small>
                                                            <h5 className="mb-0 fw-bold mt-1">
                                                                {totalRecords}
                                                            </h5>
                                                        </Col>
                                                        <Col md={3} className="border-end">
                                                            <small
                                                                className={`text-muted ${skin === "dark" ? "text-light" : ""
                                                                    }`}
                                                            >
                                                                Total Quantity
                                                            </small>
                                                            <h5 className="mb-0 fw-bold mt-1 text-info">
                                                                {totalQuantity}
                                                            </h5>
                                                        </Col>
                                                        <Col md={3} className="border-end">
                                                            <small
                                                                className={`text-muted ${skin === "dark" ? "text-light" : ""
                                                                    }`}
                                                            >
                                                                Total Discount
                                                            </small>
                                                            <h5 className="mb-0 fw-bold mt-1 text-warning">
                                                                ₹{totalDiscount.toFixed(2)}
                                                            </h5>
                                                        </Col>
                                                        <Col md={3}>
                                                            <small
                                                                className={`text-muted ${skin === "dark" ? "text-light" : ""
                                                                    }`}
                                                            >
                                                                Grand Total
                                                            </small>
                                                            <h5 className="mb-0 fw-bold mt-1 text-primary">
                                                                ₹{grandTotal.toFixed(2)}
                                                            </h5>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Results Section */}
                            {!clicked ? (
                                <div className="d-flex justify-content-center align-items-center py-5 my-5">
                                    <div className="text-center">
                                        <i className="bi bi-globe2 fs-1 text-muted"></i>
                                        <h5 className="text-muted mt-3">
                                            Please select filters and generate the report
                                        </h5>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Pagination Controls - Top */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <span className="me-2 text-muted">Show</span>
                                            <Form.Select
                                                style={{ width: "auto" }}
                                                value={recordsPerPage}
                                                onChange={(e) =>
                                                    setRecordsPerPage(Number(e.target.value))
                                                }
                                                className="border-secondary"
                                            >
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                            </Form.Select>
                                            <span className="ms-2 text-muted">entries</span>
                                        </div>
                                        <Pagination className="mb-0">
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />
                                            {renderPaginationItems()}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                        </Pagination>
                                    </div>

                                    {/* Table */}
                                    <div className="table-responsive border rounded-3">
                                        <Table
                                            striped
                                            bordered
                                            hover
                                            className={`mb-0 ${skin === "dark" ? "table-dark" : ""}`}
                                        >
                                            <thead
                                                className={`${skin === "dark" ? "bg-dark" : "bg-light"
                                                    }`}
                                            >
                                                <tr>
                                                    <th width="50">#</th>
                                                    <th>Product</th>
                                                    <th width="120">Platform</th>
                                                    <th width="110">Date</th>
                                                    <th width="160">Outlet</th>
                                                    <th width="80" className="text-center">
                                                        Qty
                                                    </th>
                                                    <th width="100" className="text-end">
                                                        Price
                                                    </th>
                                                    <th width="100" className="text-end">
                                                        Discount
                                                    </th>
                                                    <th width="110" className="text-end">
                                                        Total
                                                    </th>
                                                    <th width="80" className="text-center">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="10" className="text-center py-4">
                                                            <Spinner animation="border" variant="primary" />
                                                            <p className="mt-2 mb-0">Loading records...</p>
                                                        </td>
                                                    </tr>
                                                ) : filteredData?.length > 0 ? (
                                                    <>
                                                        {filteredData.map((record, index) => (
                                                            <tr
                                                                key={record._id}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <td
                                                                    className="text-muted"
                                                                    onClick={() => viewRecordDetails(record)}
                                                                >
                                                                    {(currentPage - 1) * recordsPerPage +
                                                                        index +
                                                                        1}
                                                                </td>
                                                                <td onClick={() => viewRecordDetails(record)}>
                                                                    <div className="fw-semibold">
                                                                        {record?.product?.name}
                                                                    </div>
                                                                    <div className="text-muted small">
                                                                        {record?.product?.classType} •{" "}
                                                                        {record?.product?.foodType}
                                                                    </div>
                                                                    {record?.addons?.length > 0 && (
                                                                        <div className="mt-1">
                                                                            {record.addons.map((addon) => (
                                                                                <Badge
                                                                                    key={addon._id}
                                                                                    bg="light"
                                                                                    text="dark"
                                                                                    className="me-1 border small"
                                                                                >
                                                                                    {addon.addonTitle}:{" "}
                                                                                    {addon.addonValueTitle}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className="text-center"
                                                                    onClick={() => viewRecordDetails(record)}
                                                                >
                                                                    <Badge
                                                                        bg={getPlatformBadgeColor(record?.platform)}
                                                                        className="py-1 px-2"
                                                                    >
                                                                        {record?.platform}
                                                                    </Badge>
                                                                </td>
                                                                <td onClick={() => viewRecordDetails(record)}>
                                                                    <div className="small">
                                                                        {new Date(
                                                                            record?.date
                                                                        ).toLocaleDateString("en-IN", {
                                                                            year: "numeric",
                                                                            month: "short",
                                                                            day: "numeric",
                                                                        })}
                                                                    </div>
                                                                </td>
                                                                <td onClick={() => viewRecordDetails(record)}>
                                                                    <div className="small fw-semibold">
                                                                        {record?.outlet?.name}
                                                                    </div>
                                                                    <div className="text-muted small">
                                                                        {record?.outlet?.city}
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    className="text-center fw-semibold"
                                                                    onClick={() => viewRecordDetails(record)}
                                                                >
                                                                    {record?.quantity}
                                                                </td>
                                                                <td
                                                                    className="text-end"
                                                                    onClick={() => viewRecordDetails(record)}
                                                                >
                                                                    ₹{record?.price?.toFixed(2)}
                                                                </td>
                                                                <td
                                                                    className="text-end text-warning"
                                                                    onClick={() => viewRecordDetails(record)}
                                                                >
                                                                    {record?.discount > 0
                                                                        ? `₹${record?.discount?.toFixed(2)}`
                                                                        : <span className="text-muted">--</span>}
                                                                </td>
                                                                <td
                                                                    className="text-end fw-bold text-primary"
                                                                    onClick={() => viewRecordDetails(record)}
                                                                >
                                                                    ₹{record?.total?.toFixed(2)}
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => viewRecordDetails(record)}
                                                                    >
                                                                        <i className="bi bi-eye"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}

                                                        {/* Grand Total Row */}
                                                        {currentPage === lastPage && (
                                                            <tr className="fw-bold">
                                                                <td colSpan="5" className="text-end">
                                                                    Grand Total:
                                                                </td>
                                                                <td className="text-center">{totalQuantity}</td>
                                                                <td></td>
                                                                <td className="text-end text-warning">
                                                                    ₹{totalDiscount.toFixed(2)}
                                                                </td>
                                                                <td className="text-end text-success">
                                                                    ₹{grandTotal.toFixed(2)}
                                                                </td>
                                                                <td></td>
                                                            </tr>
                                                        )}
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" className="text-center py-4">
                                                            <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                            <p className="mt-2 mb-0 text-muted">
                                                                No online sales records found for the selected
                                                                criteria
                                                            </p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination Controls - Bottom */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted">
                                            Showing {(currentPage - 1) * recordsPerPage + 1} to{" "}
                                            {Math.min(
                                                currentPage * recordsPerPage,
                                                totalRecords
                                            )}{" "}
                                            of {totalRecords} entries
                                        </div>
                                        <Pagination>
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />
                                            {renderPaginationItems()}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                        </Pagination>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* View Record Details Modal */}
                <Modal show={viewDetailsModal} onHide={closeModal} size="lg" centered>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="w-100 text-center">
                            <div className="d-flex justify-content-center align-items-center">
                                <span className="fs-4 me-2">🌐</span>
                                <span className="fw-bold text-muted">ONLINE SALE DETAILS</span>
                                <span className="fs-4 ms-2">📊</span>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedRecord ? (
                            <div className="p-3">
                                <Row className="mb-3">
                                    <Col xs={4}>
                                        <h6>Platform</h6>
                                        <Badge
                                            bg={getPlatformBadgeColor(selectedRecord?.platform)}
                                            className="py-1 px-3 fs-6"
                                        >
                                            {selectedRecord?.platform}
                                        </Badge>
                                    </Col>
                                    <Col xs={4}>
                                        <h6>Date</h6>
                                        <p>
                                            {new Date(selectedRecord?.date).toLocaleDateString(
                                                "en-IN",
                                                { year: "numeric", month: "long", day: "numeric" }
                                            )}
                                        </p>
                                    </Col>
                                    <Col xs={4}>
                                        <h6>Outlet</h6>
                                        <p className="fw-semibold">
                                            {selectedRecord?.outlet?.name}
                                        </p>
                                        <p className="text-muted small">
                                            {selectedRecord?.outlet?.address}
                                        </p>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <h6>Product Details</h6>
                                        <Table bordered size="sm">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-semibold" width="150">
                                                        Name
                                                    </td>
                                                    <td>{selectedRecord?.product?.name}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Category Type</td>
                                                    <td>{selectedRecord?.product?.classType}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Food Type</td>
                                                    <td>
                                                        <Badge
                                                            bg={
                                                                selectedRecord?.product?.foodType === "VEG"
                                                                    ? "success"
                                                                    : "danger"
                                                            }
                                                        >
                                                            {selectedRecord?.product?.foodType}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">MRP</td>
                                                    <td>₹{selectedRecord?.product?.mrp}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Offer Price</td>
                                                    <td>₹{selectedRecord?.product?.offerPrice}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Tax %</td>
                                                    <td>{selectedRecord?.product?.taxPercent}%</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>

                                {selectedRecord?.addons?.length > 0 && (
                                    <Row className="mb-3">
                                        <Col xs={12}>
                                            <h6>Add-ons</h6>
                                            <Table bordered size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Addon</th>
                                                        <th>Selected Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedRecord.addons.map((addon) => (
                                                        <tr key={addon._id}>
                                                            <td>{addon.addonTitle}</td>
                                                            <td>{addon.addonValueTitle}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                )}

                                <Row className="mb-2">
                                    <Col xs={12}>
                                        <h6>Pricing Summary</h6>
                                        <Table bordered size="sm">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-semibold" width="150">
                                                        Unit Price
                                                    </td>
                                                    <td>₹{selectedRecord?.price?.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Quantity</td>
                                                    <td>{selectedRecord?.quantity}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Discount</td>
                                                    <td className="text-warning">
                                                        {selectedRecord?.discount > 0
                                                            ? `₹${selectedRecord?.discount?.toFixed(2)}`
                                                            : "No discount"}
                                                    </td>
                                                </tr>
                                                <tr className="fw-bold">
                                                    <td>Total</td>
                                                    <td className="text-primary">
                                                        ₹{selectedRecord?.total?.toFixed(2)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <p>No record details available.</p>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default OnlineSalesReport;